import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Autocomplete,
  Avatar,
  Box,
  Chip,
  TextField,
  Typography,
} from "@mui/material";
import axios from "axios";
import { accountChipSx } from "./accountColors";
import { contactInitial, stringToColor } from "./contactDisplay";
import {
  parseOneRecipient,
  parseRecipientField,
} from "./whatsappEmail/threadGrouping";

const API = process.env.REACT_APP_API_URL;

function stringToRecipients(value) {
  return parseRecipientField(value).map((r) => ({
    email: r.email,
    name: r.name || r.email,
  }));
}

function recipientsToString(recipients) {
  return recipients
    .map((r) => String(r.email || "").trim())
    .filter(Boolean)
    .join(",");
}

function normalizeFreeInput(input) {
  const raw = String(input || "").trim();
  if (!raw) return null;
  const parsed = parseOneRecipient(raw);
  if (parsed?.email) {
    return { email: parsed.email, name: parsed.name || parsed.email };
  }
  return null;
}

function optionLabel(option) {
  if (typeof option === "string") return option;
  if (!option?.email) return option?.name || "";
  if (option.name && option.name.toLowerCase() !== option.email.toLowerCase()) {
    return `${option.name} <${option.email}>`;
  }
  return option.email;
}

export default function RecipientField({
  label,
  value,
  onChange,
  sx = {},
  disabled = false,
}) {
  const [recipients, setRecipients] = useState(() => stringToRecipients(value));
  const [options, setOptions] = useState([]);
  const [inputValue, setInputValue] = useState("");
  const [loading, setLoading] = useState(false);
  const debounceRef = useRef(null);
  const lastEmittedRef = useRef(value);

  useEffect(() => {
    if (value === lastEmittedRef.current) return;
    setRecipients(stringToRecipients(value));
    lastEmittedRef.current = value;
  }, [value]);

  const emitChange = useCallback(
    (nextRecipients) => {
      const normalized = recipientsToString(nextRecipients);
      lastEmittedRef.current = normalized;
      onChange(normalized);
    },
    [onChange]
  );

  const fetchSuggestions = useCallback(async (q) => {
    try {
      setLoading(true);
      const res = await axios.get(`${API}/emails/contacts/suggest`, {
        params: { q, limit: 20 },
      });
      setOptions(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      console.warn("No se pudieron cargar sugerencias de contacto:", err);
      setOptions([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      fetchSuggestions(inputValue.trim());
    }, 220);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [inputValue, fetchSuggestions]);

  const selectedEmails = useMemo(
    () => new Set(recipients.map((r) => r.email.toLowerCase())),
    [recipients]
  );

  const filteredOptions = useMemo(
    () =>
      options.filter(
        (opt) => opt?.email && !selectedEmails.has(opt.email.toLowerCase())
      ),
    [options, selectedEmails]
  );

  const commitRecipients = (next) => {
    const deduped = [];
    const seen = new Set();
    for (const item of next) {
      const normalized =
        typeof item === "string" ? normalizeFreeInput(item) : item;
      if (!normalized?.email) continue;
      const key = normalized.email.toLowerCase();
      if (seen.has(key)) continue;
      seen.add(key);
      deduped.push({
        email: normalized.email,
        name: normalized.name || normalized.email,
      });
    }
    setRecipients(deduped);
    emitChange(deduped);
  };

  return (
    <Autocomplete
      multiple
      freeSolo
      disabled={disabled}
      options={filteredOptions}
      value={recipients}
      inputValue={inputValue}
      loading={loading}
      filterOptions={(x) => x}
      onInputChange={(_e, next, reason) => {
        if (reason === "input" || reason === "clear") setInputValue(next);
      }}
      onChange={(_e, newValue, reason) => {
        if (reason === "clear") {
          commitRecipients([]);
          return;
        }
        commitRecipients(newValue);
        setInputValue("");
      }}
      getOptionLabel={optionLabel}
      isOptionEqualToValue={(a, b) =>
        String(a?.email || "").toLowerCase() === String(b?.email || "").toLowerCase()
      }
      renderOption={(props, option) => (
        <Box component="li" {...props} key={option.email}>
          <Avatar
            sx={{
              width: 28,
              height: 28,
              mr: 1.25,
              fontSize: 11,
              bgcolor: stringToColor(option.name || option.email),
            }}
          >
            {contactInitial(option.name, option.email)}
          </Avatar>
          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Typography sx={{ fontSize: 13, fontWeight: 600, lineHeight: 1.2 }}>
              {option.name || option.email}
            </Typography>
            <Typography sx={{ fontSize: 11.5, color: "text.secondary" }}>
              {option.email}
            </Typography>
          </Box>
        </Box>
      )}
      renderTags={(tagValue, getTagProps) =>
        tagValue.map((option, index) => {
          const chipTone = accountChipSx(option.email);
          return (
            <Chip
              {...getTagProps({ index })}
              key={`${option.email}-${index}`}
              size="small"
              label={option.name || option.email}
              avatar={
                <Avatar
                  sx={{
                    width: 22,
                    height: 22,
                    fontSize: 10,
                    bgcolor: stringToColor(option.name || option.email),
                    color: "#fff",
                  }}
                >
                  {contactInitial(option.name, option.email)}
                </Avatar>
              }
              sx={{
                height: 28,
                border: "1px solid",
                ...chipTone,
                "& .MuiChip-label": { fontSize: 12, fontWeight: 600 },
              }}
            />
          );
        })
      }
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          size="small"
          placeholder="Escribe un nombre o email…"
          onKeyDown={(e) => {
            if (e.key !== "Enter" && e.key !== "," && e.key !== ";") return;
            const parsed = normalizeFreeInput(inputValue);
            if (!parsed) return;
            e.preventDefault();
            commitRecipients([...recipients, parsed]);
            setInputValue("");
          }}
        />
      )}
      sx={{ mb: 1, ...sx }}
    />
  );
}
